<template>
  <div class="cuentas-wrapper">
    <ModalLoading :isLoading="isLoading" />

    <div class="opciones">
      <div class="search-bar">
        <input
          class="busqueda"
          type="text"
          v-model="searchQuery"
          placeholder="Buscar proveedor..."
        />
      </div>

      <div class="status-filter">
        <label for="status-select">Estado: </label>
        <select id="status-select" v-model="selectedStatus" class="status-select">
          <option value="">Todos</option>
          <option value="Pendiente">Pendientes</option>
          <option value="Pagado">Pagados</option>
          <option value="Parcial">Pagos Parciales</option>
        </select>
      </div>

      <!-- Filtro sucursal CEO -->
      <div v-if="esCeo" class="status-filter">
        <label>Sucursal:</label>
        <select class="form-select" style="min-width:160px" v-model="filtroSucursal" @change="onCambioSucursal">
          <option value="">Todas las sucursales</option>
          <option v-for="s in sucursalesCeo" :key="s.id_sucursal" :value="s.id_sucursal">
            {{ s.nombre_administrativo }}
          </option>
        </select>
      </div>

      <ExportButton
        :columns="columns"
        :rows="allFilteredRows"
        fileName="CuentasPorPagar.pdf"
        class="export-button"
      />
    </div>

    <div class="table-container" ref="table">
      <div v-if="loading" class="loading-indicator">Cargando cuentas por pagar...</div>

      <div v-else-if="!paginatedCuentas || paginatedCuentas.length === 0" class="no-data">
        No se encontraron cuentas por pagar.
      </div>

      <table v-else class="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Proveedor</th>
            <th>RTN</th>
            <th>Total Compras</th>
            <th>Monto Original</th>
            <th>Monto Pagado</th>
            <th>Saldo Pendiente</th>
            <th>Estado</th>
            <th>Última Actualización</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(cuenta, index) in paginatedCuentas" :key="cuenta.id_proveedor || index">
            <td data-label="#">
              {{ String((currentPage - 1) * pageSize + index + 1).padStart(3, " ") }}
            </td>
            <td data-label="Proveedor">{{ cuenta.nombre_proveedor }}</td>
            <td data-label="RTN">{{ cuenta.rtn_proveedor }}</td>
            <td data-label="Total Compras">{{ cuenta.total_facturas }}</td>
            <td data-label="Monto Original">{{ formatCurrency(cuenta.monto_original_total) }}</td>
            <td data-label="Monto Pagado">{{ formatCurrency(cuenta.monto_pagado_total) }}</td>
            <td data-label="Saldo Pendiente">{{ formatCurrency(cuenta.saldo_pendiente_total) }}</td>
            <td data-label="Estado">
              <span
                :class="{
                  'estado-badge': true,
                  'estado-pendiente': cuenta.estado === 'Pendiente',
                  'estado-pagado': cuenta.estado === 'Pagado',
                  'estado-parcial': cuenta.estado === 'Parcial',
                }"
              >{{ cuenta.estado }}</span>
            </td>
            <td data-label="Última Actualización">{{ formatDateTime(cuenta.updated_at) }}</td>
            <td class="actions-column" data-label="Acciones">
              <button
                v-if="puedePagarCxP"
                class="accion-btn accion-pagar"
                @click="abrirModalPago(cuenta)"
                :title="cuenta.estado === 'Pagado' ? 'Cuenta ya pagada' : 'Registrar Pago'"
                :disabled="cuenta.estado === 'Pagado'"
              >
                <i class="bi bi-cash-coin"></i>
              </button>
              <button
                class="accion-btn accion-historial"
                @click="verHistorial(cuenta)"
                title="Ver Historial"
              >
                <i class="bi bi-clock-history"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-wrapper">
        <div class="pagination-info">
          Mostrando {{ (currentPage - 1) * pageSize + 1 }} a
          {{ Math.min(currentPage * pageSize, filteredCuentas.length) }} de
          {{ filteredCuentas.length }} registros
        </div>
        <div class="pagination-container">
          <button class="pagination-button" :disabled="currentPage === 1" @click="previousPage">Anterior</button>
          <button class="pagination-button" :disabled="currentPage === totalPages" @click="nextPage">Siguiente</button>
        </div>
      </div>
    </div>

    <PagarProveedorModal
      v-if="isPagoModalOpen"
      :isVisible="isPagoModalOpen"
      :proveedor="proveedorSeleccionado"
      :id_usuario="id_usuario"
      @close="cerrarModalPago"
      @pago-realizado="handlePagoRealizado"
    />

    <HistorialPagosProveedorModal
      v-if="isHistorialModalOpen"
      :isVisible="isHistorialModalOpen"
      :proveedor="proveedorSeleccionado"
      :id_usuario="id_usuario"
      @close="cerrarModalHistorial"
    />
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import ExportButton from "../components/ExportButton.vue";
import PagarProveedorModal from "../components/PagarProveedorModal.vue";
import HistorialPagosProveedorModal from "../components/HistorialPagosProveedorModal.vue";
import solicitudes from "../../services/solicitudes.js";
import { hasAccionPermiso } from '../../services/permisos.js';
import CuentasPorPagarService, { obtenerCuentasPorPagarPorSucursal } from "../../services/cuentasPorPagarSolicitudes.js";
import { getSucursalesbyEmmpresaSumm } from "../../services/sucursalesSolicitudes.js";
import { setPageTitle } from "@/components/pageMetadata";
const { getSocket } = require('@/services/socket');
import ModalLoading from "@/components/ModalLoading.vue";

export default {
  name: "CuentasPorPagar",
  components: {
    ExportButton,
    PagarProveedorModal,
    HistorialPagosProveedorModal,
    ModalLoading,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      titulo: "Cuentas por Pagar",
      searchQuery: "",
      selectedStatus: "",
      id_usuario: "",
      esCeo: false,
      sucursalesCeo: [],
      filtroSucursal: "",
      proveedorSeleccionado: null,
      isPagoModalOpen: false,
      isHistorialModalOpen: false,
      isLoading: false,
      cuentas: [],
      currentPage: 1,
      pageSize: 10,
      loading: false,
      columns: [
        { header: "#", dataKey: "index" },
        { header: "Proveedor", dataKey: "nombre_proveedor" },
        { header: "RTN", dataKey: "rtn_proveedor" },
        { header: "Total Compras", dataKey: "total_facturas" },
        { header: "Monto Original", dataKey: "monto_original_total" },
        { header: "Monto Pagado", dataKey: "monto_pagado_total" },
        { header: "Saldo Pendiente", dataKey: "saldo_pendiente_total" },
        { header: "Estado", dataKey: "estado" },
        { header: "Última Actualización", dataKey: "updated_at" },
      ],
    };
  },
  computed: {
    puedePagarCxP() { return hasAccionPermiso('cxp_pagar'); },
    filteredCuentas() {
      if (!Array.isArray(this.cuentas)) return [];
      return this.cuentas.filter(c => {
        if (!c || !c.nombre_proveedor) return false;
        const matchSearch =
          c.nombre_proveedor.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (c.rtn_proveedor || '').includes(this.searchQuery);
        const matchStatus = !this.selectedStatus || c.estado === this.selectedStatus;
        return matchSearch && matchStatus;
      });
    },
    paginatedCuentas() {
      const filtered = this.filteredCuentas;
      const start = (this.currentPage - 1) * this.pageSize;
      return filtered.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredCuentas.length / this.pageSize));
    },
    allFilteredRows() {
      return this.filteredCuentas.map((c, i) => ({
        index: i + 1,
        nombre_proveedor: c.nombre_proveedor || '',
        rtn_proveedor: c.rtn_proveedor || '',
        total_facturas: c.total_facturas || 0,
        monto_original_total: this.formatCurrency(c.monto_original_total || 0),
        monto_pagado_total: this.formatCurrency(c.monto_pagado_total || 0),
        saldo_pendiente_total: this.formatCurrency(c.saldo_pendiente_total || 0),
        estado: c.estado || '',
        updated_at: this.formatDateTime(c.updated_at || new Date()),
      }));
    },
  },
  methods: {
    formatCurrency(v) { return `L. ${Number(v).toFixed(2)}`; },
    formatDateTime(d) {
      return new Date(d).toLocaleString("es-HN", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: true,
      }).replace(",", "");
    },

    async cargarCuentasPorPagar() {
      this.loading = true;
      try {
        const response = await CuentasPorPagarService.obtenerCuentasPorPagar(this.id_usuario, this.filtroSucursal || null);
        this.cuentas = Array.isArray(response) ? response : [];
      } catch (error) {
        this.toast.error("No se pudieron cargar las cuentas por pagar.");
        this.cuentas = [];
      } finally {
        this.loading = false;
      }
    },

    abrirModalPago(cuenta) {
      if (cuenta.estado === "Pagado") {
        this.toast.info("Esta cuenta ya está completamente pagada");
        return;
      }
      this.proveedorSeleccionado = cuenta;
      this.isPagoModalOpen = true;
    },
    cerrarModalPago() { this.isPagoModalOpen = false; this.proveedorSeleccionado = null; },
    verHistorial(cuenta) { this.proveedorSeleccionado = cuenta; this.isHistorialModalOpen = true; },
    cerrarModalHistorial() { this.isHistorialModalOpen = false; this.proveedorSeleccionado = null; },

    async handlePagoRealizado() {
      this.toast.success("Pago registrado exitosamente");
      await this.cargarCuentasPorPagar();
      this.cerrarModalPago();
    },

    async onCambioSucursal() {
      this.currentPage = 1;
      if (this.filtroSucursal) {
        await this.cargarCuentasPorPagarSucursal(this.filtroSucursal);
      } else {
        await this.cargarCuentasPorPagar();
      }
    },

    async cargarCuentasPorPagarSucursal(id_sucursal) {
      this.loading = true;
      try {
        const response = await obtenerCuentasPorPagarPorSucursal(id_sucursal);
        this.cuentas = Array.isArray(response) ? response : [];
      } catch (error) {
        this.toast.error('No se pudieron cargar las cuentas por pagar.');
        this.cuentas = [];
      } finally {
        this.loading = false;
      }
    },

    previousPage() { if (this.currentPage > 1) this.currentPage--; },
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; },
  },
  watch: {
    searchQuery() { this.currentPage = 1; },
    selectedStatus() { this.currentPage = 1; },
  },
  async mounted() {
    setPageTitle("Cuentas por Pagar");
    this.isLoading = true;
    try {
      this.id_usuario = await solicitudes.solicitarUsuarioToken();
      const rol = Number(localStorage.getItem('role') || 0);
      this.esCeo = rol === 4;
      if (this.esCeo) {
        this.sucursalesCeo = await getSucursalesbyEmmpresaSumm(this.id_usuario);
        if (!Array.isArray(this.sucursalesCeo)) this.sucursalesCeo = [];
      }
      await this.cargarCuentasPorPagar();
    } catch (error) {
      this.toast.error("Error al inicializar la página: " + error.message);
      this.cuentas = [];
    } finally {
      this.isLoading = false;
    }
    const _s = getSocket();
    if (_s) { this._wsRefresh = () => this.cargarCuentasPorPagar(); _s.on('cuentas-pagar:refresh', this._wsRefresh); }
  },
  beforeUnmount() {
    const _s = getSocket();
    if (_s && this._wsRefresh) _s.off('cuentas-pagar:refresh', this._wsRefresh);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* { font-family: "Montserrat", sans-serif; box-sizing: border-box; }

/* ── Page Layout ── */
.page-container { padding: 16px; width: 100%; overflow-x: hidden; background: #ffffff; min-height: 100vh; color: #212529; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.page-header h1 { font-size: 1.25rem; font-weight: 700; margin: 0 0 4px; color: #212529; }
.subtitle { font-size: 0.875rem; color: #6c757d; margin: 0; }
.opciones { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }

/* ── Inputs ── */
.busqueda, .form-input { padding: 8px 12px; font-size: 14px; border-radius: 10px; border: 1px solid #ced4da; outline: none; background: white; color: #212529; transition: all 0.3s ease; }
input:focus, select:focus, textarea:focus { border-color: #c09d62; box-shadow: 0 0 0 3px rgba(192,157,98,0.2); outline: none; }
.form-select, select { padding: 8px 12px; border-radius: 10px; border: 1px solid #ced4da; font-family: "Montserrat", sans-serif; font-size: 14px; cursor: pointer; background: white; color: #212529; }
.form-textarea { width: 100%; padding: 8px 12px; border-radius: 10px; border: 1px solid #ced4da; font-family: "Montserrat", sans-serif; font-size: 14px; resize: vertical; outline: none; background: white; color: #212529; }

/* ── Buttons ── */
.btn-primary { background-color: #c09d62; color: white; padding: 8px 16px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.3s ease; font-family: "Montserrat", sans-serif; }
.btn-primary:hover { background-color: #a38655; transform: scale(1.02); }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.btn-cancel, .btn-secondary { background-color: #6c757d; color: white; padding: 8px 16px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.3s ease; font-family: "Montserrat", sans-serif; }
.btn-cancel:hover, .btn-secondary:hover { background-color: #5a6268; }
.btn-danger { background-color: #dc3545; color: white; padding: 8px 16px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all 0.3s ease; }
.btn-danger:hover { background-color: #b02a37; }
.btn-icon { width: 32px; height: 32px; border-radius: 8px; border: 1px solid #dee2e6; background: white; color: #495057; cursor: pointer; font-size: 14px; display: inline-flex; align-items: center; justify-content: center; transition: all 0.3s ease; }
.btn-icon:hover { background: #f8f9fa; transform: scale(1.05); }
.btn-icon.success { background: #d1e7dd; color: #0f5132; border-color: #a3cfbb; }
.btn-icon.success:hover { background: #0f5132; color: white; }
.btn-icon.danger { background: #f8d7da; color: #842029; border-color: #f1aeb5; }
.btn-icon.danger:hover { background: #842029; color: white; }
.btn-icon.info { background: #cff4fc; color: #055160; border-color: #9eeaf9; }
.btn-icon.info:hover { background: #055160; color: white; }
.btn-icon:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.btn-close-x { background: none; border: none; cursor: pointer; font-size: 1rem; color: #6c757d; padding: 4px; border-radius: 6px; display: flex; align-items: center; }
.btn-close-x:hover { color: #212529; background: #f8f9fa; }

/* ── Loading / Empty ── */
.loading { display: flex; justify-content: center; padding: 3rem; }
.spinner { width: 36px; height: 36px; border: 3px solid #f3f3f3; border-top: 3px solid #c09d62; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 3rem; gap: 12px; color: #6c757d; text-align: center; }
.empty-state i { font-size: 2.5rem; opacity: 0.4; }

/* ── Table ── */
.table-container { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; margin-bottom: 20px; border-radius: 8px; border: 1px solid #e2e8f0; }
.table-container::-webkit-scrollbar { width: 8px; height: 8px; }
.table-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.table-container::-webkit-scrollbar-thumb { background: #c09d62; border-radius: 4px; }
.table-container::-webkit-scrollbar-thumb:hover { background: #a38655; }
.data-table, .table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.data-table th, .table th { padding: 8px 12px; text-align: left; font-weight: 700; font-size: 0.8rem; border-bottom: 2px solid #dee2e6; background: #f8f9fa; position: sticky; top: 0; z-index: 1; white-space: nowrap; color: #495057; }
.data-table td, .table td { padding: 8px 12px; border-bottom: 1px solid #dee2e6; vertical-align: middle; color: #212529; background: white; }
.data-table tbody tr:last-child td, .table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover td, .table tbody tr:hover td { background-color: #f8f9fa; }

/* ── Texto ── */
.text-danger { color: #dc3545 !important; }
.text-warning { color: #856404 !important; }
.text-success { color: #0f5132 !important; }
.text-muted { color: #6c757d; }

/* ── Badges ── */
.estado-badge, .badge { padding: 3px 8px; font-size: 0.75rem; border-radius: 12px; font-weight: 600; display: inline-block; }
.estado-pendiente, .badge-info, .badge-pendiente { background-color: #fff3cd; color: #856404; }
.estado-pagado, .badge-success, .badge-pagado { background-color: #d1e7dd; color: #0f5132; }
.estado-parcial, .badge-parcial { background-color: #d1ecf1; color: #0c5460; }
.badge-danger, .estado-rechazado, .badge-rechazado, .estado-vencido { background-color: #f8d7da; color: #842029; }
.badge-facturada, .estado-facturada, .badge-facturado { background-color: #e2e8f0; color: #495057; }

/* ── Acciones ── */
.actions-column, .actions { display: flex; gap: 8px; justify-content: center; align-items: center; }

.btn {
  width: 35px; height: 35px; border-radius: 10px; color: white;
  display: flex; align-items: center; justify-content: center;
  padding: 0; border: none; cursor: pointer; font-size: 16px;
  transition: all 0.3s ease;
}
.btn:hover { transform: scale(1.05); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }
/* Botones de acción en tabla */
.accion-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #fff;
  font-size: 0.85rem;
  margin: 0 2px;
  transition: opacity 0.15s, transform 0.15s;
}
.accion-btn:hover { opacity: .85; transform: scale(1.08); }
.accion-btn:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }

.accion-pagar    { background-color: #198754; }
.accion-historial { background-color: #3b82f6; }

/* ── Modal ── */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9000; padding: 1rem; }
.modal-box, .modal-content, .modal-large { background: white; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.2); width: 100%; max-width: 600px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
.modal-large { max-width: 750px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6; background: #f8f9fa; }
.modal-header h2, .modal-header h3 { font-size: 1rem; font-weight: 700; margin: 0; color: #212529; }
.modal-body { padding: 1.25rem; overflow-y: auto; flex: 1; background: white; }
.modal-footer { padding: 1rem 1.25rem; border-top: 1px solid #dee2e6; display: flex; gap: 8px; justify-content: flex-end; background: #f8f9fa; }

/* ── Forms ── */
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
.form-group { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
.form-group label { font-size: 0.8rem; font-weight: 600; color: #495057; }
.form-group .form-input, .form-group .form-select, .form-group textarea { width: 100%; }

/* ── Pagination ── */
.pagination-wrapper { display: flex; justify-content: space-between; align-items: center; padding: 1rem; border-top: 1px solid #dee2e6; }
.pagination-info { color: #6c757d; font-size: 0.875rem; }
.pagination-container { display: flex; gap: 5px; }
.pagination-button { padding: 6px 14px; border: 1px solid #dee2e6; background: white; cursor: pointer; border-radius: 6px; font-size: 0.875rem; font-family: "Montserrat", sans-serif; transition: all 0.3s ease; }
.pagination-button:hover, .pagination-button.active { background: #c09d62; color: white; border-color: #c09d62; }
.pagination-button:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 640px) {
  .form-row { grid-template-columns: 1fr; }
  .page-header { flex-direction: column; }
}

/* ── Dark Mode ── */
.dark .page-container { background: #1e1e1e; color: #e5e5e5; }
.dark .page-header h1 { color: #e5e5e5; }
.dark .subtitle { color: #9e9e9e; }
.dark .form-input, .dark .busqueda { background: #2d2d2d; color: #e5e5e5; border-color: #404040; }
.dark .form-select, .dark select { background: #2d2d2d; color: #e5e5e5; border-color: #404040; }
.dark .form-textarea { background: #2d2d2d; color: #e5e5e5; border-color: #404040; }
.dark .table-container { border-color: #404040; }
.dark .data-table th, .dark .table th { background: #2d2d2d; color: #9e9e9e; border-color: #404040; }
.dark .data-table td, .dark .table td { background: #1e1e1e; color: #e5e5e5; border-color: #404040; }
.dark .data-table tbody tr:hover td, .dark .table tbody tr:hover td { background: #2d2d2d; }
.dark .pagination-wrapper { border-color: #404040; }
.dark .pagination-info { color: #9e9e9e; }
.dark .pagination-button { background: #2d2d2d; color: #e5e5e5; border-color: #404040; }
.dark .pagination-button:hover:not(:disabled), .dark .pagination-button.active { background: #c09d62; color: white; border-color: #c09d62; }
.dark .btn-icon { background: #2d2d2d; color: #9e9e9e; border-color: #404040; }
.dark .btn-icon:hover { background: #3d3d3d; }
.dark .btn-close-x { color: #9e9e9e; }
.dark .btn-close-x:hover { color: #e5e5e5; background: #3d3d3d; }
.dark .form-group label { color: #9e9e9e; }
.dark .modal-box, .dark .modal-content, .dark .modal-large { background: #1e1e1e; }
.dark .modal-header { background: #2d2d2d; border-color: #404040; }
.dark .modal-header h2, .dark .modal-header h3 { color: #e5e5e5; }
.dark .modal-body { background: #1e1e1e; }
.dark .modal-footer { background: #2d2d2d; border-color: #404040; }
.dark .empty-state { color: #9e9e9e; }
</style>